import { OrderListItem } from 'app/src/features/UserAccount/components/OrderListItem'

import type { GenericOrderItem, OrderListFilters } from 'app/src/model'
import { refundApi, type OrderListSortKind, type RefundStatus, type StatusConfigType } from 'app/src/services/welfareportal'
import { ListItemRenderer } from 'app/src/shared/components/ListItemRenderer'
import { NavigatePaths, useTranslation, useWebAppContext } from 'app/src/shared/hooks'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useState, type Dispatch, type ReactElement, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'

export function UserAccountRefundOrderListSection(props: {
  filtersToSend: OrderListFilters
  refundStatusList: StatusConfigType[]
  updateFilters: Dispatch<SetStateAction<OrderListFilters>>
}): ReactElement {
  const globalT = useTranslation('home')
  const { t } = useTranslation('userAccount', { keyPrefix: 'Refund' })
  const context = useWebAppContext()
  const { pathname } = useLocation()

  const refundListResponse = refundApi.useRefundRequestList(
    {
      queryParams: {
        sort: props.filtersToSend.sort as OrderListSortKind,
        ...(props.filtersToSend.status !== undefined && { status: props.filtersToSend.status as RefundStatus[] }),
        ...(props.filtersToSend.from !== undefined && { from: props.filtersToSend.from }),
        ...(props.filtersToSend.to !== undefined && { to: props.filtersToSend.to }),
        ...(props.filtersToSend.offset !== undefined && { pagingOffset: props.filtersToSend.offset }),
        ...(props.filtersToSend.limit !== undefined && { pagingLimit: props.filtersToSend.limit }),
        membershipId: props.filtersToSend.membershipId,
      },
    },
    {
      enabled: !!props.filtersToSend?.membershipId && pathname === NavigatePaths.UserAccount,
    },
  )

  const [itemsToDisplay, setItemsToDisplay] = useState<refundApi.Refund[]>()

  useEffect(() => {
    if (
      refundListResponse &&
      !refundListResponse.isError &&
      refundListResponse.data?.paging &&
      refundListResponse.data.paging.offset > 0
    ) {
      setItemsToDisplay([...(itemsToDisplay ?? []), ...(refundListResponse.data?.items ?? [])])
    } else if (refundListResponse.data?.items && refundListResponse.data?.paging?.offset === 0) {
      setItemsToDisplay(refundListResponse.data.items)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refundListResponse.data])

  return (
    <>
      {itemsToDisplay && itemsToDisplay.length > 0 ? (
        <ListItemRenderer
          pt={{
            container: {
              className: 'grid gap-4',
            },
            paginator: {
              renderedInitialPageSize: context.portalConfiguration.paginatorConfig.renderedInitialPageSize ?? 1000,
              initialPageSize: context.portalConfiguration.paginatorConfig.initialPageSize ?? 1000,
            },
          }}
          data={{ paging: refundListResponse?.data?.paging, items: itemsToDisplay }}
          loadMore={(limit: number, offset: number) => {
            props.updateFilters({
              ...props.filtersToSend,
              limit: limit,
              offset: offset,
            })
          }}
          loading={refundListResponse?.isLoading}
          renderItem={(item, i) => {
            const genericItem: GenericOrderItem = {
              orderDate: item.date,
              productTypology: item.kind,
              price: item.requestedTotal,
              priceLabel: t('PriceLabel'),
              productName: item.category!,
              status: props.refundStatusList.find((i) => i.key == item.status.value)?.label ?? 'N/A',
              seeDetailsLabel: t('SeeDetailsLabel'),
              detailsLink: item.detailUrl,
            }
            return <OrderListItem key={i} item={genericItem}></OrderListItem>
          }}
        />
      ) : !refundListResponse.isLoading ? (
        <div className='mt-10 grid text-center font-bold'>{globalT.t('Global.Paginator.EmptyMessage')}</div>
      ) : (
        <div className='card justify-content-center flex'>
          <ProgressSpinner data-testid='loading-element' aria-label={globalT.t('Global.Loading.Message')} />
        </div>
      )}
    </>
  )
}
